import React, { useState, useContext, useEffect } from "react";
import Sidebar from "../sidebar";
import { useNavigate } from "react-router-dom";
import { ProgressContext } from "../ContextAPI";
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import ActionButtons from "../InitialKickoff/MaterialTopics/ActionButtons";
import { Add as AddIcon } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { companyId } from "../../../utils/companyId";
import { api } from "../../../utils/api";
import {
  textError,
  numberError,
  percentageError,
} from "../../../utils/validationMessage";
import SkipQuestionare from "../../Common/SkipQuestionare";
import InitialQuestionareSidebar from "../../../Layout/InitialQuestionareSidebar";
import { getQuestionareSubmitted } from "../../../utils/storage";

const SupplyChain = () => {
  const companyDetails = getQuestionareSubmitted();

  const { setUpdate } = useContext(ProgressContext);
  const navigate = useNavigate();

  // State to manage form data
  const [numberOfSuppliersAssessed, setNumberOfSuppliersAssessed] =
    useState("");
  const [percentageOfSuppliersAssessed, setPercentageOfSuppliersAssessed] =
    useState("");

  // State for fair labor practices
  const [fairLaborPractices, setFairLaborPractices] = useState([
    { having: "", notHaving: "" },
  ]);

  // State for child labor practices
  const [childLaborPractices, setChildLaborPractices] = useState([
    { having: "", notHaving: "" },
  ]);
  const [isExist, setIsExist] = useState(false);
  const [errors, setErrors] = useState({
    numberOfSuppliers: "",
    percentageOfSuppliers: "",
    fairLabor: [],
    childLabor: [],
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState({ type: "", index: null });
  const [isFirstFairLaborFilled, setIsFirstFairLaborFilled] = useState(false);
  const [isFirstChildLaborFilled, setIsFirstChildLaborFilled] = useState(false);
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);
  // Add this useEffect to check if index 0 is filled
  useEffect(() => {
    const fairFilled =
      fairLaborPractices[0]?.having !== "" &&
      fairLaborPractices[0]?.notHaving !== "" &&
      !isNaN(fairLaborPractices[0]?.having) &&
      !isNaN(fairLaborPractices[0]?.notHaving);

    const childFilled =
      childLaborPractices[0]?.having !== "" &&
      childLaborPractices[0]?.notHaving !== "" &&
      !isNaN(childLaborPractices[0]?.having) &&
      !isNaN(childLaborPractices[0]?.notHaving);

    setIsFirstFairLaborFilled(fairFilled);
    setIsFirstChildLaborFilled(childFilled);
  }, [fairLaborPractices, childLaborPractices]);

  useEffect(() => {
    setUpdate("SOCIAL");

    const fetchSupplyChainData = async () => {
      try {
        const response = await axios.get(
          `${api}/social/supply_chain/${companyId}`
        );
        const supplyChainData = response.data;

        setNumberOfSuppliersAssessed(
          supplyChainData?.supplierSocialAssessments
            ?.numberOfSuppliersAssessed ?? ""
        );
        setPercentageOfSuppliersAssessed(
          supplyChainData?.supplierSocialAssessments
            ?.percentageOfSuppliersAssessed?.value ?? ""
        );

        setFairLaborPractices(
          supplyChainData?.fairLaborPractices?.length
            ? supplyChainData.fairLaborPractices.map((practice) => ({
                having:
                  practice.numberOfSuppliersHavingFairLaborPractices ?? "",
                notHaving:
                  practice.numberOfSuppliersNotHavingFairLaborPractices ?? "",
              }))
            : [{ having: "", notHaving: "" }]
        );

        setChildLaborPractices(
          supplyChainData?.childLaborPractices?.length
            ? supplyChainData.childLaborPractices.map((practice) => ({
                having:
                  practice.numberOfSuppliersHavingFairLaborPractices ?? "",
                notHaving:
                  practice.numberOfSuppliersNotHavingFairLaborPractices ?? "",
              }))
            : [{ having: "", notHaving: "" }]
        );

        // Reset validation errors when loading data
        setErrors({
          numberOfSuppliers: "",
          percentageOfSuppliers: "",
          fairLabor: [],
          childLabor: [],
        });

        setIsExist(true);
      } catch (error) {
        console.error(
          "Error fetching Supply Chain Social Responsibility data:",
          error
        );
      }
    };

    if (companyId) {
      fetchSupplyChainData();
    }
  }, [companyId]);

  const validateForm = () => {
    const newErrors = {
      numberOfSuppliers: "",
      percentageOfSuppliers: "",
      fairLabor: fairLaborPractices.map(() => ({ having: "", notHaving: "" })),
      childLabor: childLaborPractices.map(() => ({
        having: "",
        notHaving: "",
      })),
    };
    let isValid = true;

    // Validate numberOfSuppliersAssessed
    // if (numberOfSuppliersAssessed === "") {
    //   newErrors.numberOfSuppliers = textError;
    //   isValid = false;
    // } else
    if (
      isNaN(numberOfSuppliersAssessed) ||
      Number(numberOfSuppliersAssessed) < 0
    ) {
      newErrors.numberOfSuppliers = numberError;
      isValid = false;
    }

    // Validate percentageOfSuppliersAssessed
    // if (percentageOfSuppliersAssessed === "") {
    //   newErrors.percentageOfSuppliers = textError;
    //   isValid = false;
    // } else
    if (
      isNaN(percentageOfSuppliersAssessed) ||
      Number(percentageOfSuppliersAssessed) < 0 ||
      Number(percentageOfSuppliersAssessed) > 100
    ) {
      newErrors.percentageOfSuppliers = percentageError;
      isValid = false;
    }

    fairLaborPractices.forEach((practice, index) => {
      // Ensure the error object exists
      if (!newErrors.fairLabor[index]) {
        newErrors.fairLabor[index] = {};
      }

      const isInvalidNumber = (val) =>
        val !== "" && (isNaN(val) || Number(val) < 0);

      if (index === 0) {
        // Optional fields – only validate if user typed something
        if (isInvalidNumber(practice.having)) {
          newErrors.fairLabor[index].having = numberError;
          isValid = false;
        }

        if (isInvalidNumber(practice.notHaving)) {
          newErrors.fairLabor[index].notHaving = numberError;
          isValid = false;
        }
      } else {
        // Required fields
        if (practice.having === "") {
          newErrors.fairLabor[index].having = textError;
          isValid = false;
        } else if (isNaN(practice.having) || Number(practice.having) < 0) {
          newErrors.fairLabor[index].having = numberError;
          isValid = false;
        }

        if (practice.notHaving === "") {
          newErrors.fairLabor[index].notHaving = textError;
          isValid = false;
        } else if (
          isNaN(practice.notHaving) ||
          Number(practice.notHaving) < 0
        ) {
          newErrors.fairLabor[index].notHaving = numberError;
          isValid = false;
        }
      }
    });

    childLaborPractices.forEach((practice, index) => {
      // Ensure the error object exists
      if (!newErrors.childLabor[index]) {
        newErrors.childLabor[index] = {};
      }

      const isInvalidNumber = (val) =>
        val !== "" && (isNaN(val) || Number(val) < 0);

      if (index === 0) {
        // Optional fields – only validate if user typed something
        if (isInvalidNumber(practice.having)) {
          newErrors.childLabor[index].having = numberError;
          isValid = false;
        }

        if (isInvalidNumber(practice.notHaving)) {
          newErrors.childLabor[index].notHaving = numberError;
          isValid = false;
        }
      } else {
        // Required fields
        if (practice.having === "") {
          newErrors.childLabor[index].having = textError;
          isValid = false;
        } else if (isNaN(practice.having) || Number(practice.having) < 0) {
          newErrors.childLabor[index].having = numberError;
          isValid = false;
        }

        if (practice.notHaving === "") {
          newErrors.childLabor[index].notHaving = textError;
          isValid = false;
        } else if (
          isNaN(practice.notHaving) ||
          Number(practice.notHaving) < 0
        ) {
          newErrors.childLabor[index].notHaving = numberError;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const addFairLaborPractice = () => {
    setFairLaborPractices([
      ...fairLaborPractices,
      { having: "", notHaving: "" },
    ]);
  };

  const addChildLaborPractice = () => {
    setChildLaborPractices([
      ...childLaborPractices,
      { having: "", notHaving: "" },
    ]);
  };

  // Function to handle Save and Continue
  const Save = async () => {
    setHasTriedToSubmit(true);
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    const payload = {
      supplierSocialAssessments: {
        ...(numberOfSuppliersAssessed !== "" && {
          numberOfSuppliersAssessed: Number(numberOfSuppliersAssessed),
        }),
        ...(percentageOfSuppliersAssessed !== "" && {
          percentageOfSuppliersAssessed: {
            value: Number(percentageOfSuppliersAssessed),
            unit: "%",
          },
        }),
      },
      fairLaborPractices: fairLaborPractices.map((practice) => ({
        ...(practice.having !== "" && {
          numberOfSuppliersHavingFairLaborPractices: Number(practice.having),
        }),
        ...(practice.notHaving !== "" && {
          numberOfSuppliersNotHavingFairLaborPractices: Number(
            practice.notHaving
          ),
        }),
      })),
      childLaborPractices: childLaborPractices.map((practice) => ({
        ...(practice.having !== "" && {
          numberOfSuppliersHavingFairLaborPractices: Number(practice.having),
        }),
        ...(practice.notHaving !== "" && {
          numberOfSuppliersNotHavingFairLaborPractices: Number(
            practice.notHaving
          ),
        }),
      })),
      companyId: companyId,
    };

    try {
      const hasValidValue = (val) =>
        val !== undefined &&
        val !== null &&
        val.toString().trim() !== "" &&
        val !== "NaN";

      const validAssessments =
        hasValidValue(
          payload.supplierSocialAssessments.numberOfSuppliersAssessed
        ) &&
        hasValidValue(
          payload.supplierSocialAssessments.percentageOfSuppliersAssessed?.value
        );

      const validFairLabor =
        Array.isArray(payload.fairLaborPractices) &&
        payload.fairLaborPractices.length > 0 &&
        hasValidValue(
          payload.fairLaborPractices[0]
            .numberOfSuppliersHavingFairLaborPractices
        ) &&
        hasValidValue(
          payload.fairLaborPractices[0]
            .numberOfSuppliersNotHavingFairLaborPractices
        );

      const validChildLabor =
        Array.isArray(payload.childLaborPractices) &&
        payload.childLaborPractices.length > 0 &&
        hasValidValue(
          payload.childLaborPractices[0]
            .numberOfSuppliersHavingFairLaborPractices
        ) &&
        hasValidValue(
          payload.childLaborPractices[0]
            .numberOfSuppliersNotHavingFairLaborPractices
        );

      payload.supply_chain =
        validAssessments && validFairLabor && validChildLabor;

      if (isExist) {
        // PUT request
        const response = await axios.put(
          `${api}/social/supply_chain/${companyId}`,
          payload
        );
      } else {
        // POST request
        await axios.post(`${api}/social/supply_chain`, payload);
      }
      navigate(
        companyDetails?.questionareSubmitted
          ? "/preview"
          : "/governance/board-composition"
      );
      setUpdate("GOVERNANCE");
    } catch (error) {
      console.error(
        "Error saving Supply Chain Social Responsibility data:",
        error
      );
    }
  };

  const deleteFairLaborPractice = (index) => {
    const updatedPractices = [...fairLaborPractices];
    updatedPractices.splice(index, 1);
    setFairLaborPractices(updatedPractices);
  };

  // Delete Child Labor Practice field
  const deleteChildLaborPractice = (index) => {
    const updatedPractices = [...childLaborPractices];
    updatedPractices.splice(index, 1);
    setChildLaborPractices(updatedPractices);
  };

  const handleOpenDialog = (type, index) => {
    setDeleteIndex({ type, index });
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    if (deleteIndex.type === "fair") {
      deleteFairLaborPractice(deleteIndex.index);
    } else if (deleteIndex.type === "child") {
      deleteChildLaborPractice(deleteIndex.index);
    }
    setOpenDialog(false);
  };

  const Back = () => {
    navigate("/social/customer-relation");
  };

  return (
    <Box sx={{ display: "flex", backgroundColor: "#EDEDED", height: "100vh" }}>
      <Box
        sx={{
          width: "25vw",
          // overflowY: "auto",
        }}
      >
        <InitialQuestionareSidebar />
      </Box>
      <Box
        sx={{
          p: 2,
          mt: 3,
          width: "75vw",
          height: "100vh", 
          overflowY: "auto", 
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "flex-start", // Adjust to start at the top of the box
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "80%",
            marginBottom: "1rem",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              width: "60%",
              fontWeight: "bold",
              zIndex: 1,
              padding: "10px 0px",
              borderRadius: "4px",
              alignSelf: "flex-start",
            }}
          >
            SOCIAL: SUPPLY CHAIN SOCIAL RESPONSIBILITY
          </Typography>
          <SkipQuestionare />
        </div>

        <Box sx={{ width: "80%" }}>
          {/* Supplier Social Assessments */}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              marginBottom: "2px",
              marginTop: "20px",
            }}
          >
            Supplier Social Assessments
          </Typography>
          <Box display="flex" gap={2} mb={2} sx={{ width: "100%" }}>
            <TextField
              variant="outlined"
              placeholder="Number of suppliers assessed"
              value={numberOfSuppliersAssessed}
              onChange={(e) => {
                setNumberOfSuppliersAssessed(e.target.value);
                setErrors({
                  ...errors,
                  numberOfSuppliers: "",
                });
              }}
              error={!!errors.numberOfSuppliers}
              // helperText={errors.numberOfSuppliers}
              helperText={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    marginLeft: "-10px",
                    color: "#D34040",
                  }}
                >
                  {errors.numberOfSuppliers}
                </Typography>
              }
              sx={{
                width: "100%",
                backgroundColor: "#EDEDED",
                borderRadius: "4px",
                borderColor: "#6b6c6d",
                "& .MuiOutlinedInput-root": {
                  height: "50px",
                  "& fieldset": {
                    borderColor: "#6b6c6d",
                  },
                },
              }}
              InputProps={{
                sx: {
                  padding: "0 10px",
                },
              }}
            />
            <TextField
              variant="outlined"
              placeholder="% of suppliers assessed"
              value={percentageOfSuppliersAssessed}
              onChange={(e) => {
                setPercentageOfSuppliersAssessed(e.target.value);
                setErrors({
                  ...errors,
                  percentageOfSuppliers: "",
                });
              }}
              error={!!errors.percentageOfSuppliers}
              // helperText={errors.percentageOfSuppliers}
              helperText={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    marginLeft: "-10px",
                    color: "#D34040",
                  }}
                >
                  {errors.percentageOfSuppliers}
                </Typography>
              }
              sx={{
                width: "100%",
                backgroundColor: "#EDEDED",
                borderRadius: "4px",
                borderColor: "#6b6c6d",
                "& .MuiOutlinedInput-root": {
                  height: "50px",
                  "& fieldset": {
                    borderColor: "#6b6c6d",
                  },
                },
              }}
              InputProps={{
                sx: {
                  padding: "0 10px",
                },
              }}
            />
          </Box>

          {/* Fair Labor Practices */}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              margin: "20px 0px 2px 0px",
            }}
          >
            Fair Labor Practices in Supply Chain
          </Typography>
          {fairLaborPractices.map((practice, index) => (
            <Box key={index} sx={{ width: "110%", display: "flex", gap: 1 }}>
              <Box
                key={index}
                display="flex"
                gap={2}
                mb={2}
                sx={{ width: "91%" }}
              >
                <TextField
                  variant="outlined"
                  placeholder="No. of suppliers having fair labor practices"
                  name="product"
                  value={practice.having}
                  onChange={(e) => {
                    const updatedPractices = [...fairLaborPractices];
                    updatedPractices[index].having = e.target.value;
                    setFairLaborPractices(updatedPractices);

                    // Clear errors for index 0 when typing
                    if (index === 0) {
                      setErrors({
                        ...errors,
                        fairLabor: {
                          ...errors.fairLabor,
                          [index]: {
                            ...errors.fairLabor[index],
                            having: "",
                          },
                        },
                      });
                    }
                  }}
                  // error={!!errors.fairLabor[index]?.having}
                  error={!!errors.fairLabor[index]?.having}
                  helperText={
                    errors.fairLabor[index]?.having && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          marginLeft: "-10px",
                          color: "#D34040",
                        }}
                      >
                        {errors.fairLabor[index]?.having}
                      </Typography>
                    )
                  }
                  multiline
                  rows={3}
                  sx={{
                    width: "100%",
                    backgroundColor: "#EDEDED",
                    borderRadius: "4px",
                    borderColor: "#6b6c6d",

                    "& .MuiOutlinedInput-root": {
                      height: "86px",
                    },
                  }}
                />
                {/* <Box display="flex" flexDirection="column" gap={2} alignItems="flex-end" width="100%"> */}
                <TextField
                  variant="outlined"
                  placeholder="No. of suppliers not having fair labor practices"
                  name="product"
                  value={practice.notHaving}
                  onChange={(e) => {
                    const updatedPractices = [...fairLaborPractices];
                    updatedPractices[index].notHaving = e.target.value;
                    setFairLaborPractices(updatedPractices);

                    // Clear errors for index 0 when typing
                    if (index === 0) {
                      setErrors({
                        ...errors,
                        fairLabor: {
                          ...errors.fairLabor,
                          [index]: {
                            ...errors.fairLabor[index],
                            notHaving: "",
                          },
                        },
                      });
                    }
                  }}
                  error={!!errors.fairLabor[index]?.notHaving}
                  helperText={
                    errors.fairLabor[index]?.notHaving && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          marginLeft: "-10px",
                          color: "#D34040",
                        }}
                      >
                        {errors.fairLabor[index]?.notHaving}
                      </Typography>
                    )
                  }
                  multiline
                  rows={3}
                  sx={{
                    width: "100%",
                    backgroundColor: "#EDEDED",
                    borderRadius: "4px",
                    borderColor: "#6b6c6d",
                    "& .MuiOutlinedInput-root": {
                      height: "86px",
                    },
                  }}
                />
              </Box>
              {index > 0 && (
                //   <Button  color="error" sx={{border: 'none'}} onClick={() => deleteFairLaborPractice(index)}>
                //     <DeleteIcon />
                //   </Button>
                <IconButton
                  sx={{ border: "none" }}
                  onClick={() => handleOpenDialog("fair", index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addFairLaborPractice}
              sx={{
                alignSelf: "flex-end",
                color: "#74757E",
                borderColor: "#74757E",
                display: "flex",
                justifyContent: "flex-end",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  color: "#115293",
                  borderColor: "#115293",
                },
              }}
              disabled={!isFirstFairLaborFilled}
            >
              Add
            </Button>
          </Box>

          {/* Child Labor Practices */}
          <Typography
            variant="subtitle1"
            sx={{
              fontSize: "16px",
              fontWeight: "bold",
              margin: "20px 0px 2px 0px",
            }}
          >
            Child Labor Practices in Supply Chain
          </Typography>
          {childLaborPractices.map((practice, index) => (
            <Box key={index} sx={{ width: "110%", display: "flex", gap: 1 }}>
              <Box
                key={index}
                display="flex"
                gap={2}
                mb={2}
                sx={{ width: "91%" }}
              >
                <TextField
                  variant="outlined"
                  placeholder="No. of suppliers having fair labor practices"
                  name="product"
                  value={practice.having}
                  onChange={(e) => {
                    const updatedPractices = [...childLaborPractices];
                    updatedPractices[index].having = e.target.value;
                    setChildLaborPractices(updatedPractices);
                    setErrors({
                      ...errors,
                      childLabor: {
                        ...errors.childLabor,
                        [index]: {
                          ...errors.childLabor[index],
                          having: "",
                        },
                      },
                    });
                  }}
                  error={!!errors.childLabor[index]?.having}
                  helperText={
                    errors.childLabor[index]?.having && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          marginLeft: "-10px",
                          color: "#D34040",
                        }}
                      >
                        {errors.childLabor[index]?.having}
                      </Typography>
                    )
                  }
                  multiline
                  rows={3}
                  sx={{
                    width: "100%",
                    backgroundColor: "#EDEDED",
                    borderRadius: "4px",
                    borderColor: "#6b6c6d",

                    "& .MuiOutlinedInput-root": {
                      height: "86px",
                    },
                  }}
                />
                {/* <Box display="flex" flexDirection="column" gap={2} alignItems="flex-end" width="100%"> */}
                <TextField
                  variant="outlined"
                  placeholder="No. of suppliers not having fair labor practices"
                  name="product"
                  value={practice.notHaving}
                  onChange={(e) => {
                    const updatedPractices = [...childLaborPractices];
                    updatedPractices[index].notHaving = e.target.value;
                    setChildLaborPractices(updatedPractices);
                    setErrors({
                      ...errors,
                      childLabor: {
                        ...errors.childLabor,
                        [index]: {
                          ...errors.childLabor[index],
                          notHaving: "",
                        },
                      },
                    });
                  }}
                  error={!!errors.childLabor[index]?.notHaving}
                  helperText={
                    errors.childLabor[index]?.notHaving && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "14px",
                          marginLeft: "-10px",
                          color: "#D34040",
                        }}
                      >
                        {errors.childLabor[index]?.notHaving}
                      </Typography>
                    )
                  }
                  multiline
                  rows={3}
                  sx={{
                    width: "100%",
                    backgroundColor: "#EDEDED",
                    borderRadius: "4px",
                    borderColor: "#6b6c6d",

                    "& .MuiOutlinedInput-root": {
                      height: "86px",
                    },
                  }}
                />
              </Box>
              {index > 0 && (
                // <Button  color="error" sx={{border: 'none'}} onClick={() => deleteChildLaborPractice(index)}>
                //     <DeleteIcon />
                // </Button>
                <IconButton
                  sx={{ border: "none" }}
                  onClick={() => handleOpenDialog("child", index)}
                >
                  <DeleteIcon />
                </IconButton>
              )}
              {/* </Box> */}
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addChildLaborPractice}
              sx={{
                alignSelf: "flex-end",
                color: "#74757E",
                borderColor: "#74757E",
                display: "flex",
                justifyContent: "flex-end",
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  color: "#115293",
                  borderColor: "#115293",
                },
              }}
              disabled={!isFirstChildLaborFilled}
            >
              Add
            </Button>
          </Box>
          <ActionButtons
            Back={Back}
            Move={Save}
            disable={companyDetails?.questionareSubmitted}
          />
        </Box>
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete this practice?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="secondary">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default SupplyChain;
